import { useState, useEffect, useRef } from 'react';
import {
  Plus, Trash2, X, CreditCard, Search, AlertCircle, Loader2, Check, Upload, Filter, ExternalLink, Eye
} from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { detectPerms } from '../../permissions';
import { ProductAutocomplete } from './forms/ProductAutocomplete';
import { NoteAllocationPicker } from './forms/NoteAllocationPicker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ── Bill type-and-search autocomplete ────────────────────────────────────────
const BillAutocomplete = ({ bills, selectedBillId, onSelect, disabled }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const selected = bills.find(b => b.id === selectedBillId);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const normalize = (s) => (s || '').toLowerCase().replace(/[\s\-\/]+/g, '');
  const terms = query.trim().split(/\s+/).filter(Boolean);
  const filtered = terms.length
    ? bills.filter(b => {
        const hay = normalize(b.bill_number) + normalize(b.vendor_name) + String(b.balance || '');
        return terms.every(t => hay.includes(normalize(t)));
      })
    : bills;

  const handleSelect = (b) => { onSelect(b.id); setQuery(''); setOpen(false); };
  const handleClear  = () => { onSelect(''); setQuery(''); };

  return (
    <div ref={ref}>
      <label className="block text-sm font-medium text-slate-700 mb-1">
        Select Purchase Bill <span className="text-slate-400 font-normal">(Optional)</span>
      </label>
      <div className={disabled ? 'opacity-60 pointer-events-none' : ''}>
        {selected && !open ? (
          <div className="flex items-center justify-between w-full px-3 py-2 border border-slate-200 rounded-sm bg-white text-sm">
            <span className="font-mono font-semibold text-slate-800">{selected.bill_number || 'No Bill #'}</span>
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <span>Bal: <b className="text-red-600">₹{(selected.balance || 0).toLocaleString('en-IN')}</b></span>
              <button type="button" onClick={handleClear} className="text-slate-400 hover:text-red-500"><X className="h-3.5 w-3.5" /></button>
            </div>
          </div>
        ) : (
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input type="text" value={query} placeholder="Type bill number or vendor name…"
              data-testid="select-bill"
              onChange={e => { setQuery(e.target.value); setOpen(true); }}
              onFocus={() => setOpen(true)}
              className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none" />
          </div>
        )}
        {open && !selected && (
          <ul className="relative z-50 w-full mt-1 bg-white border border-slate-200 rounded-sm shadow-lg max-h-52 overflow-y-auto">
            {filtered.length === 0 && <li className="px-3 py-2 text-slate-400 text-sm">No unpaid bills found</li>}
            {filtered.map(b => (
              <li key={b.id} onMouseDown={() => handleSelect(b)}
                className="px-3 py-2 hover:bg-orange-50 cursor-pointer border-b border-slate-100 last:border-0">
                <div className="flex justify-between items-center">
                  <span className="font-mono text-sm font-semibold text-slate-800">{b.bill_number || 'No Bill #'}</span>
                  <span className="text-xs text-red-600 font-bold">₹{(b.balance || 0).toLocaleString('en-IN')}</span>
                </div>
                {b.vendor_name && <div className="text-xs text-slate-500 mt-0.5">{b.vendor_name}</div>}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

const getLinkedBills = (txn, bills) => {
  const allocatedIds = (txn.allocations || [])
    .filter(allocation => allocation.reference_type === 'purchase_bill' && allocation.reference_id)
    .map(allocation => allocation.reference_id);
  const referenceIds = [...new Set(allocatedIds.length ? allocatedIds : (txn.reference_id ? [txn.reference_id] : []))];

  return referenceIds.map(referenceId => {
    const bill = bills.find(item => item.id === referenceId);
    const allocation = (txn.allocations || []).find(item => item.reference_id === referenceId);
    return {
      id: referenceId,
      label: bill?.bill_number
        || allocation?.reference_label
        || (referenceId === txn.reference_id ? txn.reference_label : null)
        || referenceId.slice(0, 8),
    };
  });
};

export const PaymentOut = () => {
  const navigate = useNavigate();
  const [bills, setBills] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [paymentModes, setPaymentModes] = useState([]);
  const [availableDebitNotes, setAvailableDebitNotes] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Filters
  const [fVendorId, setFVendorId] = useState('');
  const [fBillId, setFBillId] = useState('');
  const [fPaymentMode, setFPaymentMode] = useState('');
  const [fCompany, setFCompany] = useState('');
  const [fDateFrom, setFDateFrom] = useState('');
  const [fDateTo, setFDateTo] = useState('');
  const [fActivePreset, setFActivePreset] = useState('');
  const [appliedF, setAppliedF] = useState({ vendorId: '', billId: '', paymentMode: '', company: '', dateFrom: '', dateTo: '' });

  // Form
  const [selectedVendorId, setSelectedVendorId] = useState('');
  const [selectedBillId, setSelectedBillId] = useState('');
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [amount, setAmount] = useState('');
  const [allocationAmount, setAllocationAmount] = useState('');
  const [allocateTxn, setAllocateTxn] = useState(null);
  const [allocateBillId, setAllocateBillId] = useState('');
  const [allocateAmount, setAllocateAmount] = useState('');
  const [paymentType, setPaymentType] = useState('Cash');
  const [debitNoteAllocations, setDebitNoteAllocations] = useState({});
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [referenceNumber, setReferenceNumber] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [notes, setNotes] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imageUrl, setImageUrl] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [selectedBankDetailId, setSelectedBankDetailId] = useState('');

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [billsRes, txnRes, vendorsRes, pmRes, compRes, debitNotesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/purchase-bills`, getAuthHeaders()),
        axios.get(`${BACKEND_URL}/api/transactions?type=payment_out`, getAuthHeaders()),
        axios.get(`${BACKEND_URL}/api/vendors`, getAuthHeaders()),
        axios.get(`${BACKEND_URL}/api/payment-modes`, getAuthHeaders()),
        axios.get(`${BACKEND_URL}/api/companies`, getAuthHeaders()),
        axios.get(`${BACKEND_URL}/api/debit-notes`, getAuthHeaders())
      ]);
      setBills(billsRes.data);
      setTransactions(txnRes.data);
      setVendors(vendorsRes.data);
      setPaymentModes(pmRes.data.filter(p => p.active));
      setCompanies((compRes.data || []).filter(c => c.active !== false));
      setAvailableDebitNotes(debitNotesRes.data || []);
      // Flatten bank accounts from all companies (with companyId + isPrimary for filtering)
      const accounts = [];
      (compRes.data || []).forEach(company => {
        (company.bank_details || []).forEach(bd => {
          if (bd.id) {
            const label = bd.type === 'UPI'
              ? `${bd.upi_id || ''} (UPI)`
              : `${bd.bank_name || 'Bank'}${bd.account_no ? ` — ${bd.account_no}` : ''}`;
            accounts.push({ id: bd.id, label, companyId: company.id, companyName: company.company_name, isPrimary: bd.is_primary || false });
          }
        });
      });
      setBankAccounts(accounts);
    } catch (err) {
      setError('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const selectedBill = bills.find(b => b.id === selectedBillId);
  const isDebitNoteMode = paymentType === 'Debit Note';
  const matchingDebitNotes = availableDebitNotes.filter(note =>
    note.vendor_id === selectedVendorId
    && note.company_id === selectedCompanyId
    && (note.available_amount || 0) > 0
  );
  const allocatedDebitNoteAmount = Object.values(debitNoteAllocations)
    .reduce((sum, value) => sum + (parseFloat(value) || 0), 0);

  useEffect(() => {
    setDebitNoteAllocations({});
  }, [selectedVendorId, selectedCompanyId, selectedBillId]);

  useEffect(() => {
    if (!isDebitNoteMode) setDebitNoteAllocations({});
  }, [isDebitNoteMode]);

  // Pre-fill amount with pending balance when bill is selected
  useEffect(() => {
    if (selectedBill?.balance != null) {
      setSelectedVendorId(selectedBill.vendor_id || '');
      setSelectedCompanyId(selectedBill.company_id || '');
      setAmount(String(selectedBill.balance));
      setAllocationAmount(String(selectedBill.balance));
    } else {
      setAllocationAmount('');
    }
  }, [selectedBillId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-select primary bank account when a mode requiring account selection is chosen
  useEffect(() => {
    const mode = paymentModes.find(pm => pm.name === paymentType);
    if (mode?.account_selection_needed) {
      if (selectedCompanyId) {
        const primary = bankAccounts.find(a => a.companyId === selectedCompanyId && a.isPrimary);
        if (primary) { setSelectedBankDetailId(primary.id); return; }
      }
      // Prefer bill-level company
      if (selectedBillId) {
        const bill = bills.find(b => b.id === selectedBillId);
        if (bill?.company_id) {
          const primary = bankAccounts.find(a => a.companyId === bill.company_id && a.isPrimary);
          if (primary) { setSelectedBankDetailId(primary.id); return; }
        }
      }
      // Fall back to vendor-level company (pick primary if only one company)
      if (selectedVendorId) {
        const ids = [...new Set(bills.filter(b => b.vendor_id === selectedVendorId && b.company_id).map(b => b.company_id))];
        if (ids.length === 1) {
          const primary = bankAccounts.find(a => a.companyId === ids[0] && a.isPrimary);
          if (primary) { setSelectedBankDetailId(primary.id); return; }
        }
      }
      setSelectedBankDetailId('');
    } else {
      setSelectedBankDetailId('');
    }
  }, [paymentType, selectedBillId, selectedVendorId, selectedCompanyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const openModal = () => {
    setSelectedVendorId('');
    setSelectedBillId('');
    setSelectedCompanyId('');
    setAmount('');
    setAllocationAmount('');
    setPaymentType('');
    setDebitNoteAllocations({});
    setPaymentDate(new Date().toISOString().split('T')[0]);
    setReferenceNumber('');
    setFormDescription('');
    setNotes('');
    setImageFile(null);
    setImageUrl('');
    setSelectedBankDetailId('');
    setError('');
    setSuccess('');
    setShowModal(true);
  };

  const handleImageUpload = async (file) => {
    if (!file) return;
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${BACKEND_URL}/api/transactions/upload-image`, formData, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'multipart/form-data' }
      });
      setImageUrl(res.data.file_url);
    } catch (err) {
      setError('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!selectedVendorId) { setError('Please select a vendor'); return; }
    if (!selectedCompanyId) { setError('Please select a company'); return; }
    const effectiveAmount = isDebitNoteMode ? allocatedDebitNoteAmount : (parseFloat(amount) || 0);
    if (isDebitNoteMode && !selectedBill) { setError('Please select a Purchase Bill when using Debit Note mode'); return; }
    if (effectiveAmount <= 0) { setError(isDebitNoteMode ? 'Please select at least one Debit Note' : 'Please enter a valid amount'); return; }
    if (isDebitNoteMode && Object.entries(debitNoteAllocations).some(([id, value]) => {
      const note = matchingDebitNotes.find(item => item.id === id);
      const allocation = parseFloat(value) || 0;
      return !note || allocation <= 0 || allocation > note.available_amount;
    })) { setError('Debit Note allocations exceed an available note balance'); return; }
    if (isDebitNoteMode && effectiveAmount > (selectedBill?.balance || 0) + 0.001) {
      setError('Debit Note allocations cannot exceed the Purchase Bill balance'); return;
    }
    if (!isDebitNoteMode && selectedBill && (!allocationAmount || parseFloat(allocationAmount) <= 0 || parseFloat(allocationAmount) > effectiveAmount || parseFloat(allocationAmount) > selectedBill.balance)) {
      setError('Allocation must fit within both the payment amount and bill balance'); return;
    }

    const selectedMode = paymentModes.find(pm => pm.name === paymentType);
    if (selectedMode?.account_selection_needed && !selectedBankDetailId) {
      setError('Please select an account for this payment mode'); return;
    }

    const selectedAccount = bankAccounts.find(a => a.id === selectedBankDetailId);

    setSubmitting(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/transactions`, {
        type: 'payment_out',
        reference_type: selectedBillId ? 'purchase_bill' : null,
        reference_id: selectedBillId || null,
        vendor_id: selectedVendorId,
        company_id: selectedCompanyId,
        amount: effectiveAmount,
        allocation_amount: selectedBillId ? (isDebitNoteMode ? effectiveAmount : parseFloat(allocationAmount)) : null,
        payment_type: paymentType,
        date: paymentDate || null,
        reference_number: referenceNumber || null,
        description: formDescription || null,
        image_url: imageUrl || null,
        notes: notes || null,
        bank_detail_id: selectedBankDetailId || null,
        bank_account_label: selectedAccount?.label || null,
        debit_note_allocations: Object.entries(debitNoteAllocations).map(([note_id, value]) => ({
          note_id,
          amount: parseFloat(value) || 0,
        }))
      }, getAuthHeaders());
      setShowModal(false);
      setSuccess(`Payment recorded successfully${response.data.receipt_number ? ` — Receipt ${response.data.receipt_number}` : ''}`);
      setTimeout(() => setSuccess(''), 3000);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to record payment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAllocate = async () => {
    if (!allocateBillId || !allocateAmount || parseFloat(allocateAmount) <= 0) return;
    try {
      await axios.post(`${BACKEND_URL}/api/transactions/${allocateTxn.id}/allocate`, {
        reference_id: allocateBillId,
        amount: parseFloat(allocateAmount),
      }, getAuthHeaders());
      setAllocateTxn(null); setAllocateBillId(''); setAllocateAmount('');
      setSuccess('Payment allocated successfully');
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to allocate payment');
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/transactions/${id}`, getAuthHeaders());
      setDeleteConfirm(null);
      setSuccess('Transaction reversed successfully');
      setTimeout(() => setSuccess(''), 3000);
      fetchData();
    } catch (err) {
      setError('Failed to delete transaction');
    }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';
  const formatDateTime = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';

  const applyFiltersF = () => setAppliedF({ vendorId: fVendorId, billId: fBillId, paymentMode: fPaymentMode, company: fCompany, dateFrom: fDateFrom, dateTo: fDateTo });
  const clearFiltersF = () => { setFVendorId(''); setFBillId(''); setFPaymentMode(''); setFCompany(''); setFDateFrom(''); setFDateTo(''); setFActivePreset(''); setAppliedF({ vendorId: '', billId: '', paymentMode: '', company: '', dateFrom: '', dateTo: '' }); };
  const hasFiltersF = appliedF.vendorId || appliedF.billId || appliedF.paymentMode || appliedF.company || appliedF.dateFrom || appliedF.dateTo;

  const companyNames = [...new Set(transactions.map(t => t.company_name).filter(Boolean))].sort();

  function calcPreset(preset) {
    const now = new Date(); let fd = '', td = '';
    const pad = n => String(n).padStart(2, '0');
    const fmt = d => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
    if (preset === 'this_month') { fd = fmt(new Date(now.getFullYear(), now.getMonth(), 1)); td = fmt(now); }
    else if (preset === 'last_month') { fd = fmt(new Date(now.getFullYear(), now.getMonth()-1, 1)); td = fmt(new Date(now.getFullYear(), now.getMonth(), 0)); }
    else if (preset === 'this_quarter') { const qs = Math.floor(now.getMonth()/3)*3; fd = fmt(new Date(now.getFullYear(), qs, 1)); td = fmt(now); }
    else if (preset === 'last_quarter') { const qs = Math.floor(now.getMonth()/3)*3-3; const y = qs<0?now.getFullYear()-1:now.getFullYear(); const qss=((qs%12)+12)%12; fd=fmt(new Date(y,qss,1)); td=fmt(new Date(y,qss+3,0)); }
    else if (preset === 'this_year') { fd = fmt(new Date(now.getFullYear(), 0, 1)); td = fmt(now); }
    else if (preset === 'last_year') { fd = fmt(new Date(now.getFullYear()-1, 0, 1)); td = fmt(new Date(now.getFullYear()-1, 11, 31)); }
    return { fd, td };
  }
  const applyPreset = (preset) => { const { fd, td } = calcPreset(preset); setFActivePreset(preset); setFDateFrom(fd); setFDateTo(td); };

  const filteredTxns = (() => {
    let r = transactions.filter(t =>
      (t.reference_label || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (t.allocations || []).some(a => (a.reference_label || '').toLowerCase().includes(searchQuery.toLowerCase())) ||
      (t.receipt_number || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (t.reference_number || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (t.vendor_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (t.company_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (t.payment_type || '').toLowerCase().includes(searchQuery.toLowerCase())
    );
    if (appliedF.vendorId) r = r.filter(t => t.vendor_id === appliedF.vendorId);
    if (appliedF.billId) r = r.filter(t => t.reference_id === appliedF.billId || (t.allocations || []).some(a => a.reference_id === appliedF.billId));
    if (appliedF.paymentMode) r = r.filter(t => t.payment_type === appliedF.paymentMode);
    if (appliedF.company) r = r.filter(t => t.company_name === appliedF.company);
    if (appliedF.dateFrom) r = r.filter(t => { const d = (t.date || t.created_at || '').split('T')[0]; return d >= appliedF.dateFrom; });
    if (appliedF.dateTo) r = r.filter(t => { const d = (t.date || t.created_at || '').split('T')[0]; return d <= appliedF.dateTo; });
    return r;
  })();

  // Only show bills that have a balance > 0, filtered by selected vendor
  const unpaidBills = bills.filter(b => (b.balance || 0) > 0 && (!selectedVendorId || b.vendor_id === selectedVendorId));

  return (
    <div data-testid="payment-out-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 uppercase tracking-tight">Payment Out</h1>
          <p className="text-slate-600 mt-1">Record vendor payments and allocate them to purchase bills</p>
        </div>
        <button onClick={openModal} data-testid="record-payment-btn"
          className="inline-flex items-center gap-2 bg-orange-500 text-white font-bold tracking-wider uppercase px-6 py-3 rounded-none hover:bg-orange-600 transition-colors">
          <Plus className="h-5 w-5" /> Record Payment
        </button>
      </div>

      {success && (
        <div className="flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-sm mb-6">
          <Check className="h-5 w-5" /><span>{success}</span>
          <button onClick={() => setSuccess('')} className="ml-auto"><X className="h-4 w-4" /></button>
        </div>
      )}
      {error && !showModal && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-6">
          <AlertCircle className="h-5 w-5" /><span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto"><X className="h-4 w-4" /></button>
        </div>
      )}

      {/* Search */}
      <div className="bg-white rounded-sm border border-slate-200 p-4 mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
          <input type="text" placeholder="Search by receipt, bill, reference, vendor, company, or payment type..."
            value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} data-testid="search-input"
            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
        </div>
      </div>
      <div className="bg-white rounded-sm border border-slate-200 p-4 mb-6">
        <div className="flex items-center gap-2 mb-3"><Filter className="h-4 w-4 text-slate-500" /><span className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Filters</span>{hasFiltersF && <button onClick={clearFiltersF} data-testid="clear-filters-btn" className="ml-auto text-xs text-red-500 hover:text-red-600 font-medium">Clear All</button>}</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
          <div><label className="block text-xs font-medium text-slate-500 uppercase mb-1">Vendor</label><ProductAutocomplete value={fVendorId} onChange={(id) => { setFVendorId(id); setFBillId(''); }} products={vendors} testId="filter-vendor" placeholder="Type vendor..." /></div>
          <div><label className="block text-xs font-medium text-slate-500 uppercase mb-1">Purchase Bill</label><ProductAutocomplete value={fBillId} onChange={setFBillId} products={(fVendorId ? bills.filter(b => b.vendor_id === fVendorId) : bills).map(b => ({ id: b.id, name: b.bill_number || b.id.slice(0,8) }))} testId="filter-bill" placeholder="Type bill number..." /></div>
          <div><label className="block text-xs font-medium text-slate-500 uppercase mb-1">Company</label><select value={fCompany} onChange={e => setFCompany(e.target.value)} data-testid="filter-company" className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none bg-white"><option value="">All</option>{companyNames.map(c => <option key={c} value={c}>{c}</option>)}</select></div>
          <div><label className="block text-xs font-medium text-slate-500 uppercase mb-1">Payment Mode</label><select value={fPaymentMode} onChange={e => setFPaymentMode(e.target.value)} data-testid="filter-payment-mode" className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none bg-white"><option value="">All</option>{paymentModes.filter(pm => pm.name !== 'Debit Note').map(pm => <option key={pm.id} value={pm.name}>{pm.name}</option>)}<option value="Debit Note">Debit Note</option></select></div>
          <div><button onClick={applyFiltersF} data-testid="apply-filters-btn" className="w-full px-4 py-2 bg-orange-500 text-white text-sm font-bold uppercase tracking-wider rounded-sm hover:bg-orange-600 transition-colors">Apply</button></div>
        </div>
        <div className="mt-3 pt-3 border-t border-slate-100">
          <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Payment Date</p>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex flex-wrap gap-1.5">
              {[{ key: 'this_month', label: 'This Month' }, { key: 'last_month', label: 'Last Month' }, { key: 'this_quarter', label: 'This Quarter' }, { key: 'last_quarter', label: 'Last Quarter' }, { key: 'this_year', label: 'This Year' }, { key: 'last_year', label: 'Last Year' }].map(p => (
                <button key={p.key} onClick={() => applyPreset(p.key)} data-testid={`preset-${p.key}`} className={`px-3 py-1.5 text-xs font-semibold rounded-sm border transition-colors ${fActivePreset === p.key ? 'bg-orange-500 text-white border-orange-500' : 'border-slate-200 text-slate-600 hover:border-orange-400 hover:text-orange-600'}`}>{p.label}</button>
              ))}
            </div>
            <div className="h-5 w-px bg-slate-200 hidden md:block" />
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center gap-1.5"><label className="text-xs text-slate-500 font-medium whitespace-nowrap">From</label><input type="date" value={fDateFrom} onChange={e => { setFDateFrom(e.target.value); setFActivePreset(''); }} data-testid="date-from" className="px-2 py-1.5 border border-slate-200 rounded-sm text-xs focus:border-orange-500 focus:outline-none" /></div>
              <div className="flex items-center gap-1.5"><label className="text-xs text-slate-500 font-medium">To</label><input type="date" value={fDateTo} onChange={e => { setFDateTo(e.target.value); setFActivePreset(''); }} data-testid="date-to" className="px-2 py-1.5 border border-slate-200 rounded-sm text-xs focus:border-orange-500 focus:outline-none" /></div>
            </div>
          </div>
        </div>
      </div>

      {/* Transaction History */}
      <div className="bg-white rounded-sm border border-slate-200">
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50">
          <h2 className="text-lg font-bold text-slate-900 uppercase tracking-tight">Transaction History</h2>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
          </div>
        ) : filteredTxns.length === 0 ? (
          <div className="text-center py-16" data-testid="empty-state">
            <CreditCard className="h-16 w-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">No payment transactions yet</h3>
            <p className="text-slate-500 mb-6">Record your first payment against a purchase bill</p>
            <button onClick={openModal}
              className="inline-flex items-center gap-2 bg-orange-500 text-white font-bold tracking-wider uppercase px-6 py-3 rounded-none hover:bg-orange-600 transition-colors">
              <Plus className="h-5 w-5" /> Record First Payment
            </button>
          </div>
        ) : (
          <>
            <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 border-b border-slate-200 bg-slate-50">
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Date</div>
              <div className="col-span-2 font-semibold text-slate-700 text-sm uppercase tracking-wider">Bill No.</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Vendor</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Company</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Receipt / Ref.</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Payment Type</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Amount</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Allocated</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Available</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Status</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Actions</div>
            </div>
            {filteredTxns.map((txn) => {
              const status = txn.allocation_status === 'Allocated'
                ? { label: 'Allocated', color: 'bg-green-100 text-green-700' }
                : txn.allocation_status === 'Partially Allocated'
                  ? { label: 'Partially Allocated', color: 'bg-yellow-100 text-yellow-700' }
                  : { label: 'Unallocated', color: 'bg-slate-100 text-slate-700' };
              const linkedBills = getLinkedBills(txn, bills);
              return (
              <div key={txn.id} data-testid={`txn-row-${txn.id}`}
                className="grid grid-cols-1 md:grid-cols-12 gap-4 px-6 py-4 border-b border-slate-100 hover:bg-slate-50 items-center">
                <div className="text-slate-600 text-sm">{txn.date ? formatDate(txn.date) : formatDateTime(txn.created_at)}</div>
                <div className="md:col-span-2 font-mono text-sm font-medium flex flex-col items-start gap-1">
                  {linkedBills.length > 0 ? linkedBills.map(bill => (
                    <button
                      key={bill.id}
                      onClick={() => navigate(`/admin/purchase/bills/${bill.id}`)}
                      className="text-orange-600 hover:text-orange-700 hover:underline flex items-center gap-1 text-left"
                      title={`Go to bill ${bill.label}`}
                    >
                      {bill.label}
                      <ExternalLink className="h-3 w-3 flex-shrink-0" />
                    </button>
                  )) : <span>-</span>}
                </div>
                <div className="text-slate-700 text-sm">{txn.vendor_name || '-'}</div>
                <div className="text-slate-700 text-sm">{txn.company_name || '-'}</div>
                <div className="text-sm font-mono">
                  <div className="font-semibold text-slate-800">{txn.receipt_number || '-'}</div>
                  {txn.reference_number && <div className="mt-1 text-xs text-slate-500">Ref: {txn.reference_number}</div>}
                </div>
                <div>
                  <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-700">
                    {txn.payment_type}
                  </span>
                </div>
                <div className="font-medium text-red-600">- ₹{txn.amount?.toLocaleString()}</div>
                <div className="text-sm font-medium text-slate-700">
                  ₹{(txn.allocated_amount || 0).toLocaleString()}
                </div>
                <div className="text-sm font-medium text-red-600">
                  ₹{(txn.unallocated_amount || 0).toLocaleString()}
                </div>
                <div>
                  <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${status.color}`}>
                    {status.label}
                  </span>
                </div>
                <div>
                  <button onClick={() => navigate(`/admin/purchase/payment-out/${txn.id}`)} data-testid={`view-txn-${txn.id}`}
                    className="p-2 text-slate-600 hover:text-orange-600 hover:bg-orange-50 rounded transition-colors" title="View payment details">
                    <Eye className="h-4 w-4" />
                  </button>
                  {(txn.unallocated_amount || 0) > 0 && (
                    <button onClick={() => { setAllocateTxn(txn); setAllocateBillId(''); setAllocateAmount(''); }}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded transition-colors" title="Allocate payment">
                      <Plus className="h-4 w-4" />
                    </button>
                  )}
                  <button onClick={() => setDeleteConfirm(txn.id)} data-testid={`delete-txn-${txn.id}`}
                    className="p-2 text-slate-600 hover:text-red-500 hover:bg-red-50 rounded transition-colors" title="Reverse">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
              );
            })}
          </>
        )}
      </div>

      {/* Record Payment Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-sm w-full max-w-3xl my-8">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="text-xl font-bold text-slate-900 uppercase tracking-tight">Record Payment Out</h2>
              <button onClick={() => setShowModal(false)} className="p-2 text-slate-400 hover:text-slate-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6">
              {error && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-6">
                  <AlertCircle className="h-5 w-5" /><span>{error}</span>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Select Vendor */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Select Vendor <span className="text-red-500">*</span>
                  </label>
                  <ProductAutocomplete value={selectedVendorId} onChange={(id) => { setSelectedVendorId(id); setSelectedBillId(''); }} products={vendors.filter(v => v.active)} testId="select-vendor" placeholder="Type vendor name..." />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Company <span className="text-red-500">*</span></label>
                  <select value={selectedCompanyId} onChange={(e) => { setSelectedCompanyId(e.target.value); setSelectedBillId(''); }}
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none bg-white">
                    <option value="">Select company...</option>
                    {companies.map(company => <option key={company.id} value={company.id}>{company.company_name}</option>)}
                  </select>
                </div>

                {/* Select Purchase Bill — type-and-search autocomplete */}
                <BillAutocomplete
                  bills={unpaidBills.filter(bill => !selectedCompanyId || bill.company_id === selectedCompanyId)}
                  selectedBillId={selectedBillId}
                  onSelect={(id) => setSelectedBillId(id)}
                  disabled={!selectedVendorId}
                />

                {/* Bill Summary - spans full width */}
                {selectedBill && (
                  <div className="md:col-span-2 bg-slate-50 border border-slate-200 rounded-sm p-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                      <div>
                        <span className="text-slate-500 block">Vendor</span>
                        <span className="font-medium text-slate-900">{selectedBill.vendor_name || '-'}</span>
                      </div>
                      <div>
                        <span className="text-slate-500 block">Bill Total</span>
                        <span className="font-medium text-slate-900">₹{selectedBill.total?.toLocaleString()}</span>
                      </div>
                      <div>
                        <span className="text-slate-500 block">Already Paid</span>
                        <span className="font-medium text-green-600">₹{selectedBill.paid_amount?.toLocaleString()}</span>
                      </div>
                      <div>
                        <span className="text-slate-500 block">Balance Due</span>
                        <span className="font-bold text-red-600">₹{selectedBill.balance?.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Amount */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    {isDebitNoteMode ? 'Debit Note Amount' : 'Amount'} <span className="text-red-500">*</span>
                  </label>
                  <input type="number" value={isDebitNoteMode ? (allocatedDebitNoteAmount || '') : amount} onChange={(e) => setAmount(e.target.value)}
                    placeholder="Enter payment amount" step="0.01" min="0"
                    readOnly={isDebitNoteMode} data-testid="input-amount" className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none read-only:bg-slate-50" />
                  {selectedBill && !isDebitNoteMode && (
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-xs text-slate-500">Max: ₹{selectedBill.balance?.toLocaleString('en-IN')}</span>
                      <button type="button" onClick={() => setAmount(String(selectedBill.balance))}
                        className="text-xs text-orange-600 hover:text-orange-700">
                        Pay full balance
                      </button>
                    </div>
                  )}
                </div>

                {selectedBill && !isDebitNoteMode && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Allocate to Bill</label>
                    <input type="number" value={allocationAmount} onChange={(e) => setAllocationAmount(e.target.value)}
                      min="0" step="0.01" max={Math.min(parseFloat(amount) || 0, selectedBill.balance || 0)}
                      className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
                    <p className="text-xs text-slate-500 mt-1">Any remaining payment stays available for later allocation.</p>
                  </div>
                )}

                {/* Payment Mode */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Payment Mode</label>
                  <select value={paymentType} onChange={(e) => { setPaymentType(e.target.value); setSelectedBankDetailId(''); }}
                    data-testid="select-payment-type" className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none">
                    <option value="">Select</option>
                    {paymentModes.filter(pm => pm.name !== 'Debit Note').map(pm => <option key={pm.id} value={pm.name}>{pm.name}</option>)}
                    <option value="Debit Note">Debit Note</option>
                  </select>
                </div>

                {isDebitNoteMode && (
                  <div className="md:col-span-2">
                    {selectedBill ? (
                      <NoteAllocationPicker
                        notes={matchingDebitNotes}
                        allocations={debitNoteAllocations}
                        onChange={setDebitNoteAllocations}
                        documentBalance={selectedBill.balance || 0}
                        label="Debit Note"
                      />
                    ) : (
                      <div className="border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                        Select a Purchase Bill to view the vendor&apos;s available Debit Notes.
                      </div>
                    )}
                  </div>
                )}

                {/* Account (shown only when mode requires it) */}
                {paymentModes.find(pm => pm.name === paymentType)?.account_selection_needed && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Account <span className="text-red-500">*</span>
                    </label>
                    {(() => {
                      // 1. Bill selected → filter by bill's company
                      const bill = bills.find(b => b.id === selectedBillId);
                      const accountCompanyId = selectedCompanyId || bill?.company_id;
                      if (accountCompanyId) {
                        const companyAccounts = bankAccounts.filter(a => a.companyId === accountCompanyId);
                        return (
                          <>
                            <select value={selectedBankDetailId} onChange={(e) => setSelectedBankDetailId(e.target.value)}
                              data-testid="select-bank-account"
                              className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none">
                              <option value="">Select account...</option>
                              {companyAccounts.map(acc => (
                                <option key={acc.id} value={acc.id}>
                                  {acc.label}{acc.isPrimary ? ' (Primary)' : ''}
                                </option>
                              ))}
                            </select>
                            {companyAccounts.length === 0 && (
                              <p className="text-xs text-amber-600 mt-1">No bank accounts found for this company. Add them under Master Data &gt; Companies.</p>
                            )}
                          </>
                        );
                      }
                      // 2. Only vendor selected → filter by companies linked to this vendor's bills
                      if (selectedVendorId) {
                        const ids = [...new Set(bills.filter(b => b.vendor_id === selectedVendorId && b.company_id).map(b => b.company_id))];
                        const companyAccounts = ids.length ? bankAccounts.filter(a => ids.includes(a.companyId)) : bankAccounts;
                        return (
                          <>
                            <select value={selectedBankDetailId} onChange={(e) => setSelectedBankDetailId(e.target.value)}
                              data-testid="select-bank-account"
                              className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none">
                              <option value="">Select account...</option>
                              {companyAccounts.map(acc => (
                                <option key={acc.id} value={acc.id}>
                                  {acc.companyName} — {acc.label}{acc.isPrimary ? ' (Primary)' : ''}
                                </option>
                              ))}
                            </select>
                            {companyAccounts.length === 0 && (
                              <p className="text-xs text-amber-600 mt-1">No bank accounts found. Add them under Master Data &gt; Companies.</p>
                            )}
                          </>
                        );
                      }
                      // 3. Nothing selected → show all
                      return (
                        <select value={selectedBankDetailId} onChange={(e) => setSelectedBankDetailId(e.target.value)}
                          data-testid="select-bank-account"
                          className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none">
                          <option value="">Select account...</option>
                          {bankAccounts.map(acc => (
                            <option key={acc.id} value={acc.id}>
                              {acc.companyName} — {acc.label}{acc.isPrimary ? ' (Primary)' : ''}
                            </option>
                          ))}
                        </select>
                      );
                    })()}
                  </div>
                )}

                {/* Date */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Date</label>
                  <input type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)}
                    data-testid="input-date" className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
                </div>

                {/* Reference Number */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Reference Number <span className="text-slate-400 font-normal">(optional)</span>
                  </label>
                  <input type="text" value={referenceNumber} onChange={(e) => setReferenceNumber(e.target.value)}
                    placeholder="e.g., Cheque No., UTR, Transaction ID"
                    data-testid="input-reference-number" className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                  <textarea value={formDescription} onChange={(e) => setFormDescription(e.target.value)} rows={2}
                    placeholder="Payment description..."
                    data-testid="input-description" className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none resize-none text-sm" />
                </div>

                {/* Image Upload */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Upload Image</label>
                  <div className="flex items-center gap-3">
                    <label className="inline-flex items-center gap-2 px-4 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50 cursor-pointer transition-colors text-sm">
                      <Upload className="h-4 w-4" />
                      {imageUrl ? 'Image Uploaded' : 'Choose File'}
                      <input type="file" className="hidden" accept=".jpg,.jpeg,.png,.pdf"
                        onChange={(e) => { setImageFile(e.target.files[0]); handleImageUpload(e.target.files[0]); }} />
                    </label>
                    {uploadingImage && <Loader2 className="h-4 w-4 animate-spin text-orange-500" />}
                    {imageUrl && <span className="text-xs text-green-600">Uploaded</span>}
                  </div>
                  {imageUrl && (
                    <div className="mt-2">
                      <img src={`${BACKEND_URL}${imageUrl}`} alt="Receipt" className="h-20 w-auto rounded border border-slate-200" />
                    </div>
                  )}
                </div>

                {/* Notes */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
                  <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
                    placeholder="Optional notes..." data-testid="input-notes"
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none resize-none text-sm" />
                </div>
              </div>

              <div className="flex gap-3 justify-end pt-6 mt-6 border-t border-slate-200">
                <button type="button" onClick={() => setShowModal(false)}
                  className="px-6 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50">Cancel</button>
                <button type="submit" disabled={submitting} data-testid="submit-payment-btn"
                  className="px-6 py-2 bg-orange-500 text-white font-bold uppercase tracking-wider rounded-sm hover:bg-orange-600 disabled:opacity-50 inline-flex items-center gap-2">
                  {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                  Record Payment
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {allocateTxn && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm p-6 max-w-lg w-full">
            <h3 className="text-xl font-bold text-slate-900 mb-1">Allocate Payment Out</h3>
            <p className="text-sm text-slate-500 mb-5">Available: ₹{(allocateTxn.unallocated_amount || 0).toLocaleString('en-IN')}</p>
            <BillAutocomplete
              bills={bills.filter(bill => (bill.balance || 0) > 0 && bill.vendor_id === allocateTxn.vendor_id && bill.company_id === allocateTxn.company_id)}
              selectedBillId={allocateBillId}
              onSelect={(id) => { setAllocateBillId(id); const bill = bills.find(item => item.id === id); setAllocateAmount(String(Math.min(allocateTxn.unallocated_amount || 0, bill?.balance || 0))); }}
            />
            <label className="block text-sm font-medium text-slate-700 mt-4 mb-1">Allocation Amount</label>
            <input type="number" min="0" step="0.01" value={allocateAmount} onChange={(e) => setAllocateAmount(e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
            <div className="flex gap-3 justify-end mt-6">
              <button onClick={() => setAllocateTxn(null)} className="px-4 py-2 border border-slate-200 text-slate-700">Cancel</button>
              <button onClick={handleAllocate} className="px-4 py-2 bg-orange-500 text-white font-semibold">Allocate</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-slate-900 mb-4">Reverse Transaction</h3>
            <p className="text-slate-600 mb-6">This will reverse every allocation and remove the payment. Continue?</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50">Cancel</button>
              <button onClick={() => handleDelete(deleteConfirm)} data-testid="confirm-reverse-btn"
                className="px-4 py-2 bg-red-500 text-white rounded-sm hover:bg-red-600">Reverse</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
